/**
 * Driverni boshqa recruiterga yoki filial pool'iga o'tkazish (menejer -- o'z filiali ichida;
 * backend tekshiradi, ro'yxat ham shu doirada keladi).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { useTransferDriver, useUserOptions } from "@/features/drivers/api";
import type { UserRef } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Field, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

const POOL = "pool";

export function TransferDriverModal({ driver, open, onOpenChange }: {
  driver: { id: number; full_name: string; owner: UserRef | null };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("menu");
  const { t: tc } = useTranslation();
  const users = useUserOptions({ role: "recruiter" }, open);
  const transfer = useTransferDriver(driver.id);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTo("");
    setNote("");
    setError(null);
  }, [open]);

  // Filial bo'yicha guruhlab; hozirgi egasi ro'yxatda yo'q
  const groups = useMemo(() => {
    const map = new Map<string, { id: number; label: string }[]>();
    for (const u of users.data?.results ?? []) {
      if (u.id === driver.owner?.id) continue;
      const group = u.branch_name || "—";
      map.set(group, [...(map.get(group) ?? []), { id: u.id, label: u.display_name }]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [users.data, driver.owner?.id]);

  async function submit() {
    if (!to) {
      setError(t("transfer.chooseUser"));
      return;
    }
    const toUser = to === POOL ? null : Number(to);
    const toLabel = to === POOL ? t("transfer.pool") : (users.data?.results.find((u) => u.id === toUser)?.display_name ?? "");
    try {
      await transfer.mutateAsync({ to_user: toUser, note: note.trim() });
      toast.success(t("transfer.done", { name: driver.full_name, to: toLabel }));
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tc("toast.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t("transfer.title", { name: driver.full_name })}
      description={t("transfer.current", { name: driver.owner?.display_name ?? t("transfer.nobody") })}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={transfer.isPending} onClick={submit}>
            {t("transfer.submit")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("transfer.to")} error={error ?? undefined}>
          <Select value={to} onChange={(e) => setTo(e.target.value)} autoFocus>
            <option value="">—</option>
            {driver.owner && <option value={POOL}>{t("transfer.pool")}</option>}
            {groups.map(([group, list]) => (
              <optgroup key={group} label={group}>
                {list.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Field label={t("transfer.note")}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
